import React from "react";
import { Control } from "react-hook-form";
import {
  Button,
  FileUploaderDropContainer,
  Select,
  SelectItem,
  TextInput,
} from "@carbon/react";
import i18next from "../../../i18n";
import {
  Currency,
  CURRENCY_OPTIONS,
  FileType,
  PurchaseRequestFilePayload,
} from "../../../types";
import { fileText } from "../../../utils/utils";
import { FileReader } from "../../../components";
import TextField from "../../../components/carbon/Form/TextField";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import type { RequestFormValues } from "./requestFormSchema";

export type RequestDetailsCardProps = {
  control: Control<RequestFormValues>;
  todayString: string;

  singleDateMode: boolean;
  onSingleDateModeChange: (single: boolean) => void;
  globalNeededFrom: string;
  onGlobalNeededFromChange: (value: string) => void;
  globalNeededTo: string;
  onGlobalNeededToChange: (value: string) => void;

  globalCurrency: Currency;
  onGlobalCurrencyChange: (currency: Currency) => void;
  globalBudgetValue: number | null;
  onGlobalBudgetValueChange: (raw: string) => void;

  files: PurchaseRequestFilePayload[];
  isUploadingAttachment: boolean;
  onAttachmentUpload: (file: File) => void;
  onRemoveFile: (fileKey: string) => void;
  disabled?: boolean;
};

/**
 * Section 1 — "Detalles de la solicitud". A two-column details card holding the request-level
 * fields: name, the shared required date (with a single/range toggle), the optional estimated
 * budget, comments, the optional request code, and attachments.
 *
 * The date and budget here are request-wide DEFAULTS that propagate to each item (the screen always
 * runs with `useGlobalDates`/`useGlobalBudget` on, so the "apply to all / per item" radios the old
 * options panel showed are gone). They go through the page's own handlers — a bridge, not plain
 * `Controller` — because changing one must also propagate into every still-inherited item's RHF row
 * (`requestGlobals.ts`, override-survives). `name`, `comments` and `purchaseRequestCode` are clean
 * RHF fields. The native `<input type="date">` is kept (zero Tailwind) for the same e2e reason the
 * rest of this flow keeps it.
 */
const RequestDetailsCard: React.FC<RequestDetailsCardProps> = ({
  control,
  todayString,
  singleDateMode,
  onSingleDateModeChange,
  globalNeededFrom,
  onGlobalNeededFromChange,
  globalNeededTo,
  onGlobalNeededToChange,
  globalCurrency,
  onGlobalCurrencyChange,
  globalBudgetValue,
  onGlobalBudgetValueChange,
  files,
  isUploadingAttachment,
  onAttachmentUpload,
  onRemoveFile,
  disabled,
}) => (
  <div className="new-request-details">
    <div className="new-request-details__col">
      <TextField
        name="name"
        label={i18next.t("purchaseRequests.fields.name")}
        control={control}
        placeholder={i18next.t("purchaseRequests.fields.name")}
      />

      <div className="new-request-details__pair">
        {/* Shared required date + single/range toggle */}
        <div className="new-request-details__field">
          <div className="new-request-details__field-head">
            <span className="cds--label">
              {i18next.t("purchaseRequests.items.columns.neededDate")}
            </span>
            <Button
              kind="ghost"
              size="sm"
              className="new-request-details__link"
              onClick={() => onSingleDateModeChange(!singleDateMode)}
            >
              {singleDateMode
                ? i18next.t("purchaseRequests.items.useRange")
                : i18next.t("purchaseRequests.items.useSingleDate")}
            </Button>
          </div>
          <div className="new-request-details__dates">
            <input
              type="date"
              aria-label={i18next.t(
                "purchaseRequests.items.columns.neededFrom"
              )}
              className="new-request-date__input"
              value={globalNeededFrom}
              min={todayString}
              onChange={(e) => onGlobalNeededFromChange(e.target.value)}
            />
            {!singleDateMode && (
              <input
                type="date"
                aria-label={i18next.t(
                  "purchaseRequests.items.columns.neededTo"
                )}
                className="new-request-date__input"
                value={globalNeededTo}
                min={globalNeededFrom || undefined}
                onChange={(e) => onGlobalNeededToChange(e.target.value)}
              />
            )}
          </div>
          <p className="new-request-details__hint">
            {i18next.t("purchaseRequests.dates.appliesToAll")}
          </p>
        </div>

        {/* Estimated budget (optional) — reference for suppliers */}
        <div className="new-request-details__field">
          <span className="cds--label">
            {`${i18next.t("purchaseRequests.budgetEstimate.label")} ${i18next.t(
              "form.optional"
            )}`}
          </span>
          <div className="new-request-details__budget">
            <Select
              id="global-currency"
              labelText={i18next.t("purchaseRequests.budgetType.currency")}
              hideLabel
              value={globalCurrency}
              onChange={(e) =>
                onGlobalCurrencyChange(e.target.value as Currency)
              }
            >
              {CURRENCY_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  text={opt.label}
                />
              ))}
            </Select>
            <TextInput
              id="global-budget-value"
              type="number"
              labelText={i18next.t(
                "purchaseRequests.items.columns.budgetValue"
              )}
              hideLabel
              placeholder={i18next.t(
                "purchaseRequests.items.columns.budgetValue"
              )}
              value={globalBudgetValue ?? ""}
              onChange={(e) => onGlobalBudgetValueChange(e.target.value)}
            />
          </div>
          <p className="new-request-details__hint">
            {i18next.t("purchaseRequests.budgetEstimate.hint")}
          </p>
        </div>
      </div>

      <TextareaField
        name="comments"
        label={i18next.t("purchaseRequests.fields.comments")}
        control={control}
        optional
        rows={4}
      />
    </div>

    <div className="new-request-details__col">
      <TextField
        name="purchaseRequestCode"
        label={i18next.t("purchaseRequests.fields.purchaseRequestCode")}
        control={control}
        optional
        placeholder="PR-2026-0001"
        helperText={i18next.t(
          "purchaseRequests.fields.purchaseRequestCodeHint"
        )}
      />

      <div className="new-request-details__field">
        <span className="cds--label">
          {`${i18next.t("purchaseRequests.files")} ${i18next.t(
            "form.optional"
          )}`}
        </span>
        <div className="new-request-details__attachments">
          {files.length > 0 && (
            <ul className="new-request-options__files">
              {files.map((file) => (
                <li key={file.clientId || file.id || file.fileURL}>
                  <span className="new-request-options__file-name">
                    {file.name ||
                      fileText((file.type as FileType) || FileType.ATTACHMENT)}
                  </span>
                  <div className="new-request-options__file-actions">
                    <FileReader fileKey={file.fileURL} />
                    <Button
                      kind="ghost"
                      size="sm"
                      onClick={() =>
                        onRemoveFile(
                          file.clientId || file.id || file.fileURL || ""
                        )
                      }
                      disabled={disabled}
                    >
                      {i18next.t("cta.delete")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <FileUploaderDropContainer
            labelText={
              isUploadingAttachment
                ? i18next.t("cta.uploading")
                : i18next.t("purchaseRequests.items.attachFiles")
            }
            onAddFiles={(_e, { addedFiles }) => {
              const file = addedFiles[0];
              if (file) onAttachmentUpload(file);
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

export default RequestDetailsCard;
